<template>
  <section class="main-section parcours-patients">
    <header class="pp-entete">
      <div class="pp-titre">
        <h1 class="p-title">Parcours patient</h1>
        <button type="button" class="pp-actualiser" title="Actualiser le parcours des patients" aria-label="Actualiser le parcours des patients" :disabled="chargement" @click="charger()"><i class="fa fa-sync-alt" :class="{ 'fa-spin': chargement }" aria-hidden="true"></i></button>
      </div>
      <p class="p-subtitle">Suivez la position et le temps de présence des patients du jour et des patients hospitalisés.</p>
    </header>
    <div class="card pp-carte"><div class="card-body">
      <div class="pp-recherche-ligne">
        <div class="pp-champ-recherche">
          <label for="pp-recherche">Rechercher un patient</label>
          <input id="pp-recherche" v-model.trim="recherche" type="search" class="ui-search-field" placeholder="Nom ou n° de dossier" autocomplete="off">
        </div>
        <label class="pp-sortis"><input v-model="avecSortis" type="checkbox" @change="charger()"> Inclure les patients sortis aujourd’hui</label>
      </div>
      <div class="pp-outils">
        <div class="pp-filtres" role="group" aria-label="Afficher">
          <button v-for="filtre in filtres" :key="filtre.id" type="button" :class="{ actif: filtreActif === filtre.id }"
            :aria-pressed="filtreActif === filtre.id ? 'true' : 'false'" @click="filtreActif = filtre.id">
            {{ filtre.libelle }} <span class="pp-nombre">{{ nombre(filtre.id) }}</span>
          </button>
        </div>
        <span class="pp-resultats" role="status">{{ visibles.length }} patient(s) affiché(s)</span>
      </div>
      <p v-if="info" class="pp-info" role="status"><i class="fa fa-check-circle" aria-hidden="true"></i><span>{{ info }}</span><button type="button" class="pp-message-action" @click="info = ''">Fermer</button></p>
      <p v-if="erreur" class="pp-erreur" role="alert">{{ erreur }} <button type="button" class="pp-message-action" @click="charger()">Réessayer</button></p>
      <div v-if="chargement && !lignes.length" class="pdtb-2 algn-center"><div class="spinner-border text-secondary" role="status"><span class="sr-only">Chargement…</span></div></div>
      <p v-else-if="!erreur && !visibles.length" class="pp-vide">Aucun patient {{ filtreActif === 'tous' ? 'pour le moment' : 'dans cette catégorie' }}.</p>
      <div v-else-if="visibles.length" class="pp-table-scroll" role="region" aria-label="Parcours des patients" tabindex="0" :aria-busy="chargement ? 'true' : 'false'">
        <table class="table pp-table">
          <thead><tr><th scope="col">Patient</th><th scope="col">Position actuelle</th><th scope="col">À cette étape</th><th scope="col">Bon généré</th><th scope="col">Depuis le bon</th><th scope="col" class="pp-actions">Actions</th></tr></thead>
          <tbody>
            <tr v-for="ligne in visibles" :key="ligne.patient_id" class="pp-ligne" @click="ouvrir(ligne)">
              <td><strong>{{ nom(ligne) }}</strong><small class="d-block text-grey">Dossier {{ ligne.code || '—' }}</small></td>
              <td>
                <span :class="['pp-pastille', 'pp-niveau-' + badge(ligne).niveau]">{{ ligne.position.libelle }}</span>
                <small v-if="ligne.sejour && ligne.sejour.chambre" class="d-block text-grey">Lit : {{ libelleChambre(ligne.sejour.chambre) }}<template v-if="ligne.sejour.lit"> · {{ ligne.sejour.lit }}</template></small>
              </td>
              <td :class="'pp-duree-' + badge(ligne).niveau">{{ badge(ligne).depuis }}</td>
              <td>{{ heure(ligne.arrivee) }}</td>
              <td>{{ surPlace(ligne) }}</td>
              <td class="pp-actions" @click.stop>
                <button v-if="role === 'infirmier' && ligne.transfert_en_cours" type="button" class="btn btn-success btn-sm btn-radius" :disabled="envoi" @click="confirmerArrivee(ligne)">Arrivé</button>
                <button type="button" class="pp-voir" :aria-label="'Voir le parcours de ' + nom(ligne)" @click="ouvrir(ligne)"><i class="fa fa-route" aria-hidden="true"></i> Parcours</button>
                <button v-if="peutSortir(ligne)" type="button" class="pp-voir pp-sortie" :disabled="sortieEnvoi" :aria-label="'Autoriser la sortie de ' + nom(ligne)" @click="ouvrirSortie(ligne)"><i class="fa fa-sign-out-alt" aria-hidden="true"></i> Autoriser la sortie</button>
                <small v-else-if="ligne.sejour && ligne.sejour.status === 'DISCHARGE_PLANNED'" class="pp-sortie-statut">Sortie autorisée — départ à confirmer</small>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div></div>

    <b-modal ref="sortie" title="Autoriser la sortie d’hospitalisation" centered hide-footer
      :no-close-on-backdrop="sortieEnvoi" :no-close-on-esc="sortieEnvoi" :hide-header-close="sortieEnvoi" @hide="avantFermerSortie">
      <template v-if="sortiePatient">
        <p><strong>{{ nom(sortiePatient) }}</strong><br>Dossier {{ sortiePatient.code || '—' }}<br>
          {{ libelleChambre(sortiePatient.sejour.chambre) || 'Chambre non renseignée' }} · {{ sortiePatient.sejour.lit || 'Lit non renseigné' }}</p>
        <p>L’infirmier confirmera le départ réel. Le lit ne sera disponible qu’après nettoyage.</p>
        <p class="small">Cette action autorise la sortie du séjour. Elle ne clôture pas la consultation médicale.</p>
        <p v-if="sortieErreur" class="pp-erreur" role="alert">{{ sortieErreur }}</p>
        <div class="ui-modal-actions">
          <button type="button" class="btn btn-secondary-light-o" :disabled="sortieEnvoi" @click="$refs.sortie.hide()">Annuler</button>
          <button type="button" class="btn btn-primary" :disabled="sortieEnvoi" @click="autoriserSortie">{{ sortieEnvoi ? 'Enregistrement…' : 'Autoriser la sortie' }}</button>
        </div>
      </template>
    </b-modal>
    <b-modal ref="detail" :title="detail ? 'Parcours de ' + nom(detail.patient || {}) : 'Parcours'" size="lg" hide-footer @hidden="detail = null">
      <p v-if="detailChargement" class="text-grey">Chargement…</p>
      <p v-else-if="detailErreur" class="text-danger">{{ detailErreur }}</p>
      <template v-else-if="detail">
        <ActionsParcours :donnees="detail" :maintenant="maintenant" @change="apresAction">
          <router-link :to="{ name: 'patientInfo', params: { id: detail.patient.id, consultation: 'consulter' } }" class="btn btn-secondary-light-o ap-bouton">Ouvrir le dossier</router-link>
        </ActionsParcours>
        <hr>
        <FriseParcours :etapes="detail.etapes" :position="detail.position" :maintenant="maintenant" />
      </template>
    </b-modal>
  </section>
</template>

<script>
import api from '@/api/consultations/_parcours'
import ActionsParcours from '@/components/parcours/actions-parcours.vue'
import FriseParcours from '@/components/parcours/frise-parcours.vue'
import { pastille, heure, duree, secondesEntre, libelleChambre } from '@/components/parcours/parcours-utils'

const FILTRES = [
  { id: 'tous', libelle: 'Tous' },
  { id: 'attente', libelle: 'En attente' },
  { id: 'consultation', libelle: 'En consultation' },
  { id: 'hospitalises', libelle: 'Hospitalisés' },
  { id: 'transfert', libelle: 'En transfert' },
]

function normaliser(texte) {
  return String(texte || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
}

// Page « Parcours patient » des soignants (médecin, infirmier), 04/10/2026.
export default {
  name: 'parcours-patients',
  components: { ActionsParcours, FriseParcours },
  data() {
    return {
      filtres: FILTRES, filtreActif: 'tous', recherche: '', avecSortis: false, lignes: [], role: '',
      chargement: false, erreur: '', info: '', envoi: false, maintenant: new Date(), minuteur: null,
      detail: null, detailChargement: false, detailErreur: '', detailPatient: null,
      sortiePatient: null, sortieErreur: '', sortieEnvoi: false,
    }
  },
  computed: {
    visibles() {
      const cherche = normaliser(this.recherche)
      return this.lignes.filter(ligne => this.correspond(ligne, this.filtreActif) &&
        (!cherche || normaliser(this.nom(ligne) + ' ' + (ligne.code || '')).indexOf(cherche) >= 0))
    },
  },
  mounted() {
    this.charger()
    let tours = 0
    this.minuteur = setInterval(() => {
      this.maintenant = new Date()
      if (++tours % 2 === 0) this.charger(true)
    }, 30000)
  },
  beforeDestroy() { clearInterval(this.minuteur) },
  methods: {
    peutSortir(ligne) {
      return this.role === 'medecin' && ligne.peut_autoriser_sortie === true && ligne.sejour && ligne.sejour.status === 'ADMITTED'
    },
    ouvrirSortie(ligne) {
      if (this.sortieEnvoi || !this.peutSortir(ligne)) return
      this.sortiePatient = Object.assign({}, ligne, { sejour: Object.assign({}, ligne.sejour) })
      this.sortieErreur = ''
      this.$refs.sortie.show()
    },
    avantFermerSortie(event) {
      if (this.sortieEnvoi) { event.preventDefault(); return }
      this.sortiePatient = null
    },
    async autoriserSortie() {
      const patient = this.sortiePatient
      if (this.sortieEnvoi || !patient || !this.peutSortir(patient)) return
      this.sortieEnvoi = true
      this.sortieErreur = ''
      try {
        const reponse = await api.autoriserSortie(patient.sejour.id)
        if (!reponse || reponse.success !== true) throw new Error('Réponse non confirmée')
        if (this._isDestroyed) return
        // Mettre l'état à jour même si le rafraîchissement réseau suivant échoue.
        const ligne = this.lignes.find(item => item.patient_id === patient.patient_id && item.sejour && item.sejour.id === patient.sejour.id)
        if (ligne) { this.$set(ligne.sejour, 'status', 'DISCHARGE_PLANNED'); this.$set(ligne, 'peut_autoriser_sortie', false) }
        this.info = 'Sortie de ' + this.nom(patient) + ' autorisée — départ à confirmer par l’infirmier.'
        this.sortieEnvoi = false
        this.$refs.sortie.hide()
        this.sortiePatient = null
        await this.charger(true)
      } catch (erreur) {
        if (!this._isDestroyed) {
          const donnees = erreur && erreur.response ? erreur.response.data : null
          this.sortieErreur = (donnees && donnees.message) || 'La sortie n’a pas pu être autorisée. Réessayez.'
        }
      } finally { this.sortieEnvoi = false }
    },
    heure(iso) { return heure(iso, this.maintenant) },
    libelleChambre,
    nom(ligne) { return [String(ligne.nom || '').toUpperCase(), ligne.prenoms].filter(Boolean).join(' ') },
    badge(ligne) { return pastille(ligne.position, this.maintenant) || { niveau: 'normal', depuis: '—' } },
    surPlace(ligne) { return ligne.position && ligne.position.code === 'sorti' ? '—' : duree(secondesEntre(ligne.arrivee, this.maintenant)) },
    correspond(ligne, filtre) {
      const code = ligne.position ? ligne.position.code : ''
      if (filtre === 'attente') return !!(ligne.position && ligne.position.attente) && code !== 'transfert'
      if (filtre === 'consultation') return code === 'consultation'
      if (filtre === 'hospitalises') return !!ligne.sejour && code !== 'sorti'
      if (filtre === 'transfert') return code === 'transfert'
      return true
    },
    nombre(filtre) { return this.lignes.filter(ligne => this.correspond(ligne, filtre)).length },
    async charger(silencieux) {
      if (!silencieux) this.chargement = true
      try {
        const reponse = await api.patientsPresents(this.avecSortis)
        this.lignes = reponse && Array.isArray(reponse.data) ? reponse.data : []
        this.role = (reponse && reponse.role) || ''
        this.erreur = ''
      } catch (erreur) {
        if (!silencieux) this.erreur = 'La liste des patients n’a pas pu être chargée.'
      } finally {
        this.chargement = false
        this.maintenant = new Date()
      }
    },
    async ouvrir(ligne) {
      this.detail = null
      this.detailErreur = ''
      this.detailChargement = true
      this.detailPatient = ligne
      if (this.$refs.detail) this.$refs.detail.show()
      await this.chargerDetail()
    },
    async chargerDetail() {
      const ligne = this.detailPatient
      if (!ligne) return
      try {
        const reponse = await api.parcoursPatient(ligne.patient_id, ligne.consultation_id)
        this.detail = reponse && reponse.data ? reponse.data : null
      } catch (erreur) {
        this.detailErreur = 'Le parcours n’a pas pu être chargé.'
      } finally {
        this.detailChargement = false
        this.maintenant = new Date()
      }
    },
    async apresAction(message) {
      this.info = message || ''
      await Promise.all([this.chargerDetail(), this.charger(true)])
    },
    async confirmerArrivee(ligne) {
      if (!ligne.transfert_en_cours) return
      this.envoi = true
      this.erreur = ''
      try {
        await api.confirmerArrivee(ligne.transfert_en_cours.id)
        this.info = 'Arrivée de ' + this.nom(ligne) + ' en ' + ligne.transfert_en_cours.vers + ' confirmée.'
        await this.charger(true)
      } catch (erreur) {
        const donnees = erreur && erreur.response ? erreur.response.data : null
        this.erreur = (donnees && donnees.message) || 'L’arrivée n’a pas pu être confirmée.'
      } finally {
        this.envoi = false
      }
    },
  },
}
</script>

<style scoped>
.parcours-patients { padding: 24px 20px; color: var(--ui-text, #172033); }
.pp-entete { margin-bottom: 22px; }
.pp-titre { display: flex; align-items: center; gap: 12px; }
.pp-titre h1 { margin: 0; font-size: 26px; font-weight: 700; }
.pp-entete .p-subtitle { margin-top: 8px; font-weight: 400; color: var(--ui-text-secondary, #273449); }
.pp-actualiser { display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; flex-shrink: 0; border: 1px solid #b6c5df; border-radius: 50%; background: white; color: var(--ui-accent, #2457c5); }
.pp-actualiser:disabled { opacity: .55; cursor: wait; }
.pp-carte { border: 1px solid #dbe4f2; border-radius: 22px; box-shadow: 0 6px 24px rgba(23,32,51,.04); }
.pp-carte .card-body { padding: 24px; }
.pp-recherche-ligne { display: flex; flex-wrap: wrap; align-items: flex-end; gap: 16px 24px; margin-bottom: 20px; }
.pp-champ-recherche { flex: 1 1 380px; min-width: 0; }
.pp-champ-recherche label { display: block; margin-bottom: 8px; font-size: 14px; font-weight: 600; }
.pp-sortis { min-height: 48px; }
.pp-sortis input { width: 18px; height: 18px; accent-color: #2457c5; }
.pp-outils { display: flex; flex-wrap: wrap; justify-content: space-between; align-items: center; gap: 12px; margin-bottom: 14px; }
.pp-filtres { display: flex; flex-wrap: wrap; gap: 8px; }
.pp-filtres button { min-height: 44px; border: 1px solid #b6c5df; background: #fff; color: var(--ui-text, #172033); border-radius: 999px; padding: 8px 14px; font-weight: 600; font-size: 14px; }
.pp-filtres button.actif { background: var(--ui-accent, #2457c5); border-color: var(--ui-accent, #2457c5); color: #fff; }
.pp-nombre { display: inline-block; min-width: 20px; margin-left: 4px; padding: 0 6px; border-radius: 999px; background: rgba(48, 66, 105, .1); font-size: 12px; text-align: center; }
.pp-filtres button.actif .pp-nombre { background: #fff; color: #2457c5; }
.pp-resultats { font-size: 13px; color: var(--ui-text-muted, #526074); }
.pp-sortis { display: inline-flex; align-items: center; gap: 6px; margin: 0; font-size: 14px; color: var(--ui-text, #4a5875); }
.pp-info, .pp-erreur { display: flex; align-items: center; flex-wrap: wrap; gap: 10px; padding: 12px 16px; border-radius: 12px; background: #edf7f1; color: #166044; font-size: 14px; }
.pp-erreur { color: #a52632; background: #fff1f2; }
.pp-message-action { margin-left: auto; border: 0; background: transparent; color: inherit; text-decoration: underline; min-height: 32px; }
.pp-vide { padding: 24px 0; color: var(--ui-text-secondary, #65728b); text-align: center; }
.pp-table-scroll { overflow-x: auto; }
.pp-table { margin: 0; color: var(--ui-text, #172033); font-size: 14px; }
.pp-table th { padding: 12px; background: #f3f6fc; color: var(--ui-text-secondary, #273449); border: 0; white-space: nowrap; font-weight: 600; }
.pp-table th:first-child { border-radius: 12px 0 0 12px; }
.pp-table th:last-child { border-radius: 0 12px 12px 0; }
.pp-table td { padding: 14px 12px; vertical-align: middle; border-top: 0; border-bottom: 1px solid #e3eaf5; }
.pp-table tr:last-child td { border-bottom: 0; }
.pp-table td:first-child { min-width: 220px; }
.pp-table td:nth-child(n+3) { white-space: nowrap; font-variant-numeric: tabular-nums; }
.pp-table small { margin-top: 5px; font-size: 12px; color: var(--ui-text-muted, #526074) !important; }
.pp-voir { display: inline-flex; align-items: center; gap: 8px; min-height: 40px; padding: 8px 14px; border: 1px solid #bdccec; border-radius: 999px; color: var(--ui-accent, #2457c5); background: #eef3ff; font-weight: 600; font-size: 14px; }
.pp-voir:hover { background: #dfe9ff; }
.pp-actualiser:focus-visible, .pp-filtres button:focus-visible, .pp-voir:focus-visible, .pp-table-scroll:focus-visible, .pp-message-action:focus-visible { outline: 3px solid #2457c5; outline-offset: 3px; }
.pp-ligne { cursor: pointer; }
.pp-ligne:hover td { background: #f7faff; }
.pp-pastille { display: inline-block; padding: 3px 10px; border-radius: 999px; font-weight: 600; font-size: 13px; }
.pp-niveau-actif { background: #e7efff; color: #2457c5; }
.pp-niveau-normal { background: #eef3fb; color: var(--ui-text, #304269); }
.pp-niveau-orange { background: #fff1e0; color: #8a4805; }
.pp-niveau-rouge { background: #fdecec; color: #b42318; }
.pp-niveau-sorti { background: #f0f2f5; color: var(--ui-text-secondary, #65728b); }
.pp-duree-orange { color: #8a4805; font-weight: 600; }
.pp-duree-rouge { color: #b42318; font-weight: 600; }
.pp-actions { text-align: right; white-space: nowrap; }
.pp-actions .btn + .btn { margin-left: 6px; }
.pp-actions .btn { border-radius: 999px; margin-right: 6px; }
.pp-sortie { margin-left: 8px; min-height: 44px; }
.pp-table small.pp-sortie-statut { display: block; white-space: normal; color: var(--ui-success, #166044) !important; }
.pp-actions .pp-sortie:disabled { cursor: wait; }
@media (max-width: 767px) { .parcours-patients { padding: 16px 8px; } .pp-carte .card-body { padding: 16px; } .pp-titre h1 { font-size: 23px; } .pp-recherche-ligne { gap: 8px; } .pp-sortis { min-height: 40px; } }
</style>
